import { useState } from "react";
import { Box, Button, Checkbox, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../Components/SnackBar";
import { copyText } from "../utils";
import { personalizeReply } from "../mockData";
import { revealSection } from "../sections";
import {
  AMBER,
  AMBER_TINT,
  CARD_BORDER,
  GREEN,
  GREEN_TINT,
  HOVER_BG,
  SOFT_BG,
  TEXT_BODY,
  TEXT_MAIN,
  TEXT_MUTED,
  TEXT_SUB,
  g,
} from "../styles";

const linkButton = {
  display: "inline",
  fontSize: "13px",
  fontWeight: 500,
  color: GREEN,
  textDecoration: "underline",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: "inherit",
};

const s = {
  card: { border: CARD_BORDER, borderRadius: "10px", backgroundColor: "#fff", overflow: "hidden" },
  head: { display: "flex", alignItems: "flex-start", gap: "4px", padding: "10px 12px" },
  headText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" },
  // Who asked and where — the first thing on the card, so the manager doesn't
  // have to remember whose question it was.
  askerRow: { display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap", minHeight: "24px" },
  askerIcon: { fontSize: 16, color: TEXT_SUB },
  askerName: { fontSize: "14px", fontWeight: 500, color: TEXT_MAIN },
  askerMeta: { fontSize: "12px", color: TEXT_SUB },
  askerDetected: { fontSize: "12px", color: TEXT_MUTED },
  askerUnknown: { fontSize: "13px", color: TEXT_SUB },
  addName: { ...linkButton, marginInlineStart: "4px" },
  editBtn: { padding: "2px", color: "rgba(62, 63, 76, 0.5)" },
  nameForm: { display: "flex", alignItems: "center", gap: "2px", flex: 1, minWidth: 0 },
  nameInput: { flex: 1, minWidth: 0, ...g.fieldSx },
  metaRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  time: { fontSize: "12px", color: TEXT_SUB },
  excerpt: {
    fontSize: "13px",
    color: TEXT_BODY,
    lineHeight: 1.45,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  iconBtn: { color: "rgba(62, 63, 76, 0.6)", "&:hover": { backgroundColor: HOVER_BG } },
  body: { borderTop: CARD_BORDER, padding: "12px", display: "flex", flexDirection: "column", gap: "12px" },
  verdict: { fontSize: "14px", fontWeight: 500, color: TEXT_MAIN, lineHeight: 1.45 },
  sectionLabel: { fontSize: "12px", fontWeight: 500, color: TEXT_SUB, marginBottom: "6px" },
  steps: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" },
  step: { display: "flex", alignItems: "flex-start", gap: "2px", fontSize: "13px", color: TEXT_BODY, lineHeight: 1.5 },
  stepCheck: { padding: "2px", marginTop: "-1px", color: "rgba(62, 63, 76, 0.45)", "&.Mui-checked": { color: GREEN } },
  stepText: { paddingTop: "2px" },
  // A done step stays readable — the manager may want to see what was done.
  stepTextDone: { paddingTop: "2px", textDecoration: "line-through", color: TEXT_MUTED },
  progress: { fontSize: "12px", color: TEXT_SUB },
  recheckRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  recheckHint: { fontSize: "12px", color: TEXT_SUB },
  stepLink: { ...linkButton, marginInlineStart: "4px" },
  variants: { display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" },
  reply: {
    fontSize: "14px",
    color: TEXT_MAIN,
    lineHeight: 1.55,
    backgroundColor: SOFT_BG,
    border: CARD_BORDER,
    borderRadius: "8px",
    padding: "10px 12px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  copyRow: { display: "flex", justifyContent: "flex-end", marginTop: "8px" },
};

const VARIANTS = ["default", "shorter", "warmer"];

const AnswerCard = ({ answer, expanded, onToggle, onDelete, onUpdate, onToggleStep, onRecheck, lang, t }) => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [variant, setVariant] = useState("default");
  const asker = answer.asker || { name: "", source: "other", detected: false };
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(asker.name);
  const ready = answer.status === "ready";
  const [rechecking, setRechecking] = useState(false);
  const doneCount = answer.steps.filter((step) => step.done).length;

  const recheck = async () => {
    setRechecking(true);
    try {
      await onRecheck();
    } finally {
      setRechecking(false);
    }
  };

  const time = new Intl.DateTimeFormat(lang, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(
    new Date(answer.createdAt),
  );
  const excerpt = answer.input.fromImage ? `${t("helper.from_image")}: ${answer.question}` : answer.input.text;

  const openStepLink = (link) => {
    if (link.startsWith("#")) {
      revealSection(link.slice(1));
    } else {
      navigate(link);
    }
  };

  const saveName = (event) => {
    event.preventDefault();
    onUpdate({ asker: { ...asker, name: nameDraft.trim(), detected: false } });
    setEditingName(false);
  };

  const cancelName = () => {
    setNameDraft(asker.name);
    setEditingName(false);
  };

  // The reply greets the asker by first name; renaming updates it.
  const reply = personalizeReply(answer.replies?.[variant], asker.name, answer.replyLang);

  return (
    <Box sx={s.card}>
      <Box sx={s.head}>
        <Box sx={s.headText}>
          <Box sx={s.askerRow}>
            <PersonOutlineIcon sx={s.askerIcon} aria-hidden="true" />
            {editingName ? (
              <Box component="form" onSubmit={saveName} sx={s.nameForm}>
                <TextField
                  size="small"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && cancelName()}
                  placeholder={t("helper.asker_placeholder")}
                  autoFocus
                  inputProps={{ "aria-label": t("helper.asker_label"), dir: "auto" }}
                  sx={s.nameInput}
                />
                <IconButton type="submit" size="small" aria-label={t("helper.save_name")}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={cancelName} aria-label={t("helper.cancel")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : asker.name ? (
              <>
                <Typography component="span" sx={s.askerName} dir="auto">{asker.name}</Typography>
                <Typography component="span" sx={s.askerMeta}>· {t(`helper.sources.${asker.source}`)}</Typography>
                {asker.detected && (
                  <Typography component="span" sx={s.askerDetected}>· {t("helper.name_detected")}</Typography>
                )}
                <IconButton size="small" onClick={() => setEditingName(true)} sx={s.editBtn} aria-label={t("helper.edit_name")}>
                  <EditOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </>
            ) : (
              <>
                <Typography component="span" sx={s.askerUnknown}>{t("helper.asker_unknown")}</Typography>
                <Box component="button" type="button" onClick={() => setEditingName(true)} sx={s.addName}>
                  {t("helper.add_name")}
                </Box>
              </>
            )}
          </Box>
          <Box sx={s.metaRow}>
            <Box component="span" sx={g.pill(ready ? GREEN : AMBER, ready ? GREEN_TINT : AMBER_TINT)}>
              {ready ? t("helper.status_ready") : t("helper.status_action")}
            </Box>
            {!ready && answer.steps.length > 0 && (
              <Typography component="span" sx={s.progress}>
                {t("helper.steps_progress", { done: doneCount, total: answer.steps.length })}
              </Typography>
            )}
            <Typography component="span" sx={s.time}>{time}</Typography>
          </Box>
          <Typography sx={s.excerpt} dir="auto">{excerpt}</Typography>
        </Box>
        <IconButton size="small" onClick={onToggle} sx={s.iconBtn} aria-expanded={expanded} aria-label={expanded ? t("helper.collapse") : t("helper.expand")}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={onDelete} sx={s.iconBtn} aria-label={t("helper.delete")}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {expanded && (
        <Box sx={s.body}>
          <Typography sx={s.verdict}>{answer.verdict}</Typography>

          {answer.steps.length > 0 && (
            <Box>
              <Typography sx={s.sectionLabel}>{t("helper.steps_title")}</Typography>
              <Box component="ol" sx={s.steps}>
                {answer.steps.map((step, i) => (
                  <Box component="li" key={i} sx={s.step}>
                    <Checkbox
                      size="small"
                      checked={!!step.done}
                      onChange={(e) => onToggleStep(i, e.target.checked)}
                      sx={s.stepCheck}
                      inputProps={{ "aria-label": `${t("helper.step_done")}: ${step.text}` }}
                    />
                    <Box component="span" sx={step.done ? s.stepTextDone : s.stepText}>
                      {step.text}
                      {step.link && (
                        <Box component="button" type="button" onClick={() => openStepLink(step.link)} sx={s.stepLink}>
                          {step.linkLabel}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {answer.replies && (
            <Box>
              <Typography sx={s.sectionLabel}>{t("helper.reply_title")}</Typography>
              <Box sx={s.variants}>
                {VARIANTS.map((v) => (
                  <Button key={v} variant="outlined" size="small" onClick={() => setVariant(v)} sx={g.toggleChip(variant === v)} aria-pressed={variant === v}>
                    {t(`helper.variant_${v}`)}
                  </Button>
                ))}
              </Box>
              <Typography sx={s.reply} dir={answer.replyLang === "he" ? "rtl" : "ltr"}>
                {reply}
              </Typography>
              <Box sx={s.copyRow}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    const copied = await copyText(reply);
                    snackbar(copied ? t("helper.copied") : t("copy_failed"), copied ? "success" : "error");
                  }}
                  sx={g.outlineBtn}
                >
                  {t("helper.copy")}
                </Button>
              </Box>
            </Box>
          )}

          {/* After acting on the steps (a supplier joined, a discount started),
              ask again against the community as it is now. */}
          <Box sx={s.recheckRow}>
            <Button
              variant="outlined"
              size="small"
              startIcon={rechecking ? <CircularProgress size={14} sx={{ color: GREEN }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={recheck}
              disabled={rechecking || !answer.input.text}
              sx={g.outlineBtn}
            >
              {rechecking ? t("helper.rechecking") : t("helper.recheck")}
            </Button>
            {!ready && <Typography component="span" sx={s.recheckHint}>{t("helper.recheck_hint")}</Typography>}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AnswerCard;
